import {
	Box,
	Button,
	Alert,
	Typography,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from "@mui/material";
import { UseTheme } from "lib/hooks";

export type DialogMaxWidth = "xs" | "sm" | "md" | "lg";

export interface UserManagementDialogShellProps {
	open: boolean;
	onClose: () => void;
	title: string;
	/** Optional sub-title or header (e.g. "Admin: Name") rendered at top of content. */
	subTitle?: React.ReactNode;
	/** Sector Admin dialogs use "xs"; Plant User dialogs use "md". */
	maxWidth?: DialogMaxWidth;
	loading?: boolean;
	error?: string;
	children: React.ReactNode;
	cancelLabel?: string;
	onCancel?: () => void;
	/** Primary action button (Save, Add, Delete, etc.) */
	primaryButton: {
		label: string;
		onClick: () => void;
		disabled?: boolean;
		/** When true, show loading state (e.g. "Saving...") */
		loading?: boolean;
		/** When true, use error color and treat as destructive (e.g. Delete). */
		destructive?: boolean;
	};
	/** Optional extra content sx for DialogContent (e.g. padding overrides). */
	contentSx?: Record<string, unknown>;
}

/**
 * Shared dialog shell for User Management dialogs (Add/Edit Sector Admin, Add/Edit Plant User).
 * Provides consistent title styling, loading spinner, error alert, and Cancel + primary action.
 * Sector Admin dialogs pass maxWidth="xs"; Plant User dialogs pass maxWidth="md".
 */
export function UserManagementDialogShell({
	open,
	onClose,
	title,
	subTitle,
	maxWidth = "xs",
	loading = false,
	error,
	children,
	cancelLabel = "Cancel",
	onCancel,
	primaryButton,
	contentSx,
}: UserManagementDialogShellProps) {
	const { theme } = UseTheme();
	const handleCancel = onCancel ?? onClose;
	const displayPrimaryLabel = primaryButton.loading
		? primaryButton.destructive
			? "Deleting..."
			: primaryButton.label === "Save"
				? "Saving..."
				: primaryButton.label === "Add"
					? "Adding..."
					: primaryButton.label
		: primaryButton.label;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth={maxWidth}
			fullWidth
			disableRestoreFocus
		>
			<DialogTitle sx={{ textAlign: "center" }}>
				<Typography variant="h4" align="center">
					{title}
				</Typography>
			</DialogTitle>
			<DialogContent
				sx={{
					padding: theme.spacing(3.5, 3),
					display: "flex",
					flexDirection: "column",
					gap: 1.5,
					...contentSx,
				}}
			>
				{loading ? (
					<Box display="flex" justifyContent="center" alignItems="center" p={3}>
						<CircularProgress />
					</Box>
				) : (
					<>
						{error && (
							<Alert severity="error" sx={{ whiteSpace: "pre-line" }}>
								{error}
							</Alert>
						)}
						{subTitle}
						{children}
					</>
				)}
			</DialogContent>
			<DialogActions sx={{ px: 3 }}>
				<Box sx={{ display: "flex", gap: 1 }}>
					<Button onClick={handleCancel} disabled={primaryButton.loading}>
						{cancelLabel}
					</Button>
					<Button
						variant="contained"
						onClick={primaryButton.onClick}
						disabled={primaryButton.disabled || primaryButton.loading}
						color={primaryButton.destructive ? "error" : "primary"}
					>
						{displayPrimaryLabel}
					</Button>
				</Box>
			</DialogActions>
		</Dialog>
	);
}
